<template>
  <div :class="{ 'theme--dark': dark, 'mt-5 mt-sm-0': dark }">
    <v-autocomplete
      v-model="internalValue"
      :items="searchResults"
      :loading="loading"
      :search-input.sync="search"
      :placeholder="placeholder"
      :label="label"
      :item-text="displayField"
      :item-value="valueField"
      :outlined="outlined"
      :dense="dense"
      :clearable="clearable"
      :disabled="disabled"
      :rules="rules"
      :hide-details="hideDetails"
      :dark="dark"
      :hide-no-data="!search || search.length < minSearchLength"
      :no-filter="true"
      :color="dark ? 'white' : 'secondary'"
      return-object
      @change="handleChange"
    >
    <template v-slot:no-data>
      <v-list-item v-if="search && search.length < minSearchLength">
        <v-list-item-content>
          <v-list-item-title>
            Type at least {{ minSearchLength }} characters to search
          </v-list-item-title>
        </v-list-item-content>
      </v-list-item>
      <v-list-item v-else-if="search && !loading && searchResults.length === 0">
        <v-list-item-content>
          <v-list-item-title>
            No results found for "{{ search }}"
          </v-list-item-title>
        </v-list-item-content>
      </v-list-item>
    </template>

    <template v-slot:item="{ item }">
      <v-list-item-content>
        <v-list-item-title>{{ item[displayField] }}</v-list-item-title>
        <v-list-item-subtitle v-if="item[subtitleField]">
          {{ item[subtitleField] }}
        </v-list-item-subtitle>
      </v-list-item-content>
    </template>

    <template v-slot:selection="{ item }">
      <span>{{ item[displayField] }}</span>
    </template>
    </v-autocomplete>
  </div>
</template>

<script>
import { debounce } from 'lodash'

export default {
  name: 'AutocompleteSearch',
  
  props: {
    value: {
      type: Object,
      default: null
    },
    searchFunction: {
      type: Function,
      required: true
    },
    placeholder: {
      type: String,
      default: 'Search...'
    },
    label: {
      type: String,
      default: ''
    },
    displayField: {
      type: String,
      default: 'Subject'
    },
    valueField: {
      type: String,
      default: 'OID'
    },
    subtitleField: {
      type: String,
      default: 'BranchName'
    },
    minSearchLength: {
      type: Number,
      default: 5
    },
    debounceDelay: {
      type: Number,
      default: 300
    },
    outlined: {
      type: Boolean,
      default: true
    },
    dense: {
      type: Boolean,
      default: false
    },
    clearable: {
      type: Boolean,
      default: true
    },
    disabled: {
      type: Boolean,
      default: false
    },
    rules: {
      type: Array,
      default: () => []
    },
    hideDetails: {
      type: Boolean,
      default: false
    },
    dark: {
      type: Boolean,
      default: false
    }
  },
  
  data() {
    return {
      internalValue: null,
      search: null,
      searchResults: [],
      loading: false
    }
  },
  
  watch: {
    value: {
      immediate: true,
      handler(newValue) {
        this.internalValue = newValue
      }
    },
    
    search(val) {
      if (!val || val.length < this.minSearchLength) {
        this.searchResults = []
        return
      }
      
      if (this.internalValue && val === this.internalValue[this.displayField]) {
        return
      }
      
      this.debouncedSearch(val)
    }
  },
  
  created() {
    this.debouncedSearch = debounce(this.performSearch, this.debounceDelay)
  },
  
  methods: {
    async performSearch(searchTerm) {
      if (!searchTerm || searchTerm.length < this.minSearchLength) {
        this.searchResults = []
        return
      }
      
      this.loading = true
      
      try {
        const results = await this.searchFunction(searchTerm)
        this.searchResults = Array.isArray(results) ? results : []
      } catch (error) {
        this.searchResults = []
        this.$emit('error', 'Search failed. Please try again.')
      } finally {
        this.loading = false
      }
    },
    
    handleChange(value) {
      this.$emit('input', value)
      this.$emit('change', value)

      if (!value) {
        this.$emit('clear')
        this.searchResults = []
      }
    }
  }
}
</script>

<style scoped>
/* ONLY apply dark styles when dark mode is explicitly enabled */
.theme--dark > .v-autocomplete.v-text-field--outlined >>> .v-input__slot {
  background-color: transparent !important;
  border-color: rgba(255, 255, 255, 0.5) !important;
}

.theme--dark > .v-autocomplete.v-text-field--outlined >>> .v-input__slot:hover {
  border-color: rgba(255, 255, 255, 0.7) !important;
}

.theme--dark > .v-autocomplete.v-text-field--outlined.v-input--is-focused >>> .v-input__slot {
  border-color: white !important;
  background-color: transparent !important;
}

.theme--dark > .v-autocomplete >>> .v-label,
.theme--dark > .v-autocomplete >>> .v-label.theme--light {
  color: rgba(255, 255, 255, 0.7) !important;
}

.theme--dark > .v-autocomplete >>> .v-label--active,
.theme--dark > .v-autocomplete >>> .v-label--active.theme--light {
  color: white !important;
}

.theme--dark > .v-autocomplete >>> input,
.theme--dark > .v-autocomplete.v-text-field >>> input {
  color: white !important;
  caret-color: white !important;
}

.theme--dark > .v-autocomplete >>> .v-icon,
.theme--dark > .v-autocomplete >>> .v-icon.theme--light {
  color: rgba(255, 255, 255, 0.7) !important;
}

.theme--dark > .v-autocomplete >>> .v-select__selections input::placeholder {
  color: rgba(255, 255, 255, 0.5) !important;
}

.theme--dark > .v-autocomplete >>> fieldset {
  border-color: rgba(255, 255, 255, 0.5) !important;
}

.theme--dark > .v-autocomplete.v-input--is-focused >>> fieldset {
  border-color: white !important;
}
</style>